import { __rest } from "tslib";
import { DriveTimeTrafficType, RoutingMode } from '@sas-dvr/ltjs-commons';
import { Select } from '@sas/nova-commons/components/Select';
import { withLabels } from '@sas/nova-commons/components/withLabels';
import React, { Fragment, useMemo, useRef, useState } from 'react';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { useI18n } from '../../../../locale/I18nContext';
import { CompositeInput } from '@sas/nova-commons/components/CompositeInput';
import { Popover } from '@sas/nova-commons/components/Popover';
import { Label } from '@sas/nova-commons/components/Label';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { DateTime, Refresh } from '@sas/react-svg';
import { useFormatLocale } from '@sas/nova-commons/locale/formatting';
import { MenuButton } from '@sas/nova-commons/components/MenuButton';
import { TimePicker } from '@sas/nova-commons/components/DatePicker/Pickers/TimePicker';
import cn from './TravelTypeField.css.json';
import './TravelTypeField.css';
// copied from @sas/nova-commons/components/DatePicker/DateUtil - as we need the full weekday name
function getDaysOfWeek(localeTag) {
    const format = new Intl.DateTimeFormat(localeTag, { weekday: 'long' });
    const weekdays = [];
    for (let day = 5; day < 12; day++) {
        const testDate = new Date(2020, 0, day, 0, 0, 0);
        weekdays.push(format.format(testDate));
    }
    return weekdays;
}
const WeekdayTimeCompositeInput = (props) => {
    const localize = useI18n();
    const formatLocale = useFormatLocale();
    const { initialValue, buttonProps, onChange } = props, restProps = __rest(props, ["initialValue", "buttonProps", "onChange"]);
    const weekdayTypes = useMemo(() => getDaysOfWeek(formatLocale).map((value, index) => {
        return { key: index, text: value };
    }), [formatLocale]);
    const [date, setDate] = useState(() => {
        var _a, _b, _c, _d, _e, _f;
        const now = new Date();
        return new Date(0, 0, (_b = (_a = props.initialValue) === null || _a === void 0 ? void 0 : _a.day) !== null && _b !== void 0 ? _b : now.getDay(), (_d = (_c = props.initialValue) === null || _c === void 0 ? void 0 : _c.hour) !== null && _d !== void 0 ? _d : now.getHours(), (_f = (_e = props.initialValue) === null || _e === void 0 ? void 0 : _e.minute) !== null && _f !== void 0 ? _f : now.getMinutes());
    });
    const entryFieldRef = useRef(null);
    const buttonRef = useRef(null);
    const [showPopover, setShowPopover] = useState(false);
    const [selectedDate, setSelectedDate] = useState(date);
    const returnFocus = () => setTimeout(() => { var _a; return (_a = entryFieldRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
    return (React.createElement(Fragment, null,
        React.createElement(CompositeInput, Object.assign({ value: date.toLocaleTimeString(formatLocale, {
                weekday: 'long',
                hour: '2-digit',
                minute: '2-digit',
            }), buttonDisplay: localize({ key: 'TravelTypeField.dateselector.tooltip.txt' }), onOpen: () => setShowPopover(true), buttonProps: Object.assign({ tooltip: localize({ key: 'TravelTypeField.button.tooltip.txt' }) }, buttonProps), mode: 'select', entryFieldRef: entryFieldRef, buttonRef: buttonRef }, restProps)),
        React.createElement(Popover, { referenceElementRef: buttonRef, isOpen: showPopover, returnFocus: false, onEscape: returnFocus, onClose: () => setShowPopover(false), isModal: true, footer: React.createElement(Flow, { spacing: "s" },
                React.createElement(Button, { onPress: () => {
                        var _a, _b;
                        setDate(selectedDate);
                        onChange === null || onChange === void 0 ? void 0 : onChange({
                            day: selectedDate.getDay(),
                            hour: (_a = selectedDate.getHours()) !== null && _a !== void 0 ? _a : 0,
                            minute: (_b = selectedDate.getMinutes()) !== null && _b !== void 0 ? _b : 0,
                        });
                        setShowPopover(false);
                        returnFocus();
                    }, text: localize({ key: 'TravelTypeField.ok.txt' }) }),
                React.createElement(Button, { onPress: () => {
                        setShowPopover(false);
                        returnFocus();
                    }, text: localize({ key: 'TravelTypeField.cancel.txt' }) })) },
            React.createElement(Flow, { className: cn.formField, orientation: "vertical", spacing: "s" },
                React.createElement(SelectWithLabels, { label: localize({ key: 'TravelTypeField.day.txt' }), selectedIndex: selectedDate.getDay(), onSelected: (selectedDayIndex) => {
                        if (selectedDayIndex !== null && selectedDayIndex !== undefined) {
                            setSelectedDate(new Date(0, 0, selectedDayIndex, selectedDate.getHours(), selectedDate.getMinutes()));
                        }
                    }, values: weekdayTypes, fieldWidth: '100%', className: cn.formField }),
                React.createElement(Label, null, localize({ key: 'TravelTypeField.time.txt' })),
                React.createElement(TimePicker, { testId: props.testId, value: selectedDate, onChange: setSelectedDate, showMilliseconds: false, showHours: true, showMinutes: true, showSeconds: false, showDayPeriod: true })))));
};
const SelectWithLabels = withLabels(Select);
const CompositeInputWithLabels = withLabels(WeekdayTimeCompositeInput);
export const TravelTypeField = (props) => {
    var _a, _b;
    const localize = useI18n();
    const travelTypes = [
        {
            key: RoutingMode.DIRECT,
            text: localize({
                key: props.feature.type === 'area'
                    ? 'TravelTypeField.distanceTypes.distance.txt'
                    : 'TravelTypeField.distanceTypes.direct.txt',
            }),
        },
    ];
    if (props.canDoPremiumServices) {
        travelTypes.push({
            key: RoutingMode.DRIVING_DISTANCE,
            text: localize({ key: 'TravelTypeField.distanceTypes.driving.distance.txt' }),
        });
        travelTypes.push({
            key: RoutingMode.DRIVING_TIME,
            text: localize({ key: 'TravelTypeField.distanceTypes.driving.time.txt' }),
        });
        travelTypes.push({
            key: RoutingMode.TRUCKING_DISTANCE,
            text: localize({ key: 'TravelTypeField.distanceTypes.trucking.distance.txt' }),
        });
        travelTypes.push({
            key: RoutingMode.TRUCKING_TIME,
            text: localize({ key: 'TravelTypeField.distanceTypes.trucking.time.txt' }),
        });
        travelTypes.push({
            key: RoutingMode.WALKING_DISTANCE,
            text: localize({ key: 'TravelTypeField.distanceTypes.walking.distance.txt' }),
        });
        travelTypes.push({
            key: RoutingMode.WALKING_TIME,
            text: localize({ key: 'TravelTypeField.distanceTypes.walking.time.txt' }),
        });
    }
    const travelTypeIndex = travelTypes.findIndex((type) => {
        return type.key === props.feature.mode;
    });
    const travelTypeSupportsTraffic = ((_a = travelTypes[travelTypeIndex]) === null || _a === void 0 ? void 0 : _a.key) === RoutingMode.DRIVING_TIME ||
        ((_b = travelTypes[travelTypeIndex]) === null || _b === void 0 ? void 0 : _b.key) === RoutingMode.TRUCKING_TIME;
    const driveTimeTrafficTypes = [
        {
            key: DriveTimeTrafficType.NONE,
            text: localize({ key: 'TravelTypeField.driveTimeTrafficTypes.none.txt' }),
        },
        {
            key: DriveTimeTrafficType.LIVE,
            text: localize({ key: 'TravelTypeField.driveTimeTrafficTypes.live.txt' }),
        },
        {
            key: DriveTimeTrafficType.HISTORICAL_AVERAGE,
            text: localize({ key: 'TravelTypeField.driveTimeTrafficTypes.historic.txt' }),
        },
    ];
    const driveTimeTrafficTypeIndex = driveTimeTrafficTypes.findIndex((type) => {
        return type.key === (props.feature.traffic || DriveTimeTrafficType.NONE);
    });
    const driveTimeTrafficOffsetTypes = [
        -4,
        -3,
        -2,
        -1,
        0,
        1,
        2,
        3,
        4,
    ].map((hour) => {
        return {
            key: hour,
            text: localize({
                key: 'TravelTypeField.driveTimeTraffic.offset.fmt',
                args: { hour: hour < 0 ? String(hour) : `+${String(hour)}` },
            }),
        };
    });
    const driveTimeTrafficOffsetIndex = driveTimeTrafficOffsetTypes.findIndex((type) => {
        var _a;
        return type.key === (((_a = props.feature.trafficTime) === null || _a === void 0 ? void 0 : _a.hour) || 0);
    });
    return (React.createElement(Fragment, null,
        travelTypes.length > 1 ? (React.createElement(Flow, { spacing: 's', orientation: 'horizontal', style: { width: '100%' } },
            React.createElement(SelectWithLabels, { label: localize({ key: 'TravelTypeField.travelType.txt' }), selectedIndex: travelTypeIndex, onSelected: (selectedIndex) => {
                    if (typeof selectedIndex !== 'number') {
                        return;
                    }
                    props.onTravelTypeChange(travelTypes[selectedIndex].key);
                }, values: travelTypes, fieldWidth: '100%', className: cn.formField }))) : null,
        travelTypeSupportsTraffic ? (React.createElement(Flow, { spacing: 's', orientation: 'vertical', style: { width: '100%' } },
            React.createElement(Flow, { spacing: 'none', orientation: 'horizontal', style: { width: '100%' } },
                React.createElement(SelectWithLabels, { label: localize({ key: 'TravelTypeField.traffic.txt' }), selectedIndex: driveTimeTrafficTypeIndex, onSelected: (selectedIndex) => {
                        if (typeof selectedIndex !== 'number') {
                            return;
                        }
                        props.onTrafficTypeChange(driveTimeTrafficTypes[selectedIndex].key);
                    }, labelHelp: {
                        library: 'vaeua-gui-eua',
                        contentKey: 'react.va.geo.search.includeTraffic',
                    }, values: driveTimeTrafficTypes, fieldWidth: '100%', className: cn.formField }),
                React.createElement(MenuButton, { sections: [
                        {
                            items: [
                                {
                                    text: localize({
                                        key: 'TravelTypeField.driveTimeTraffic.update.refresh.txt',
                                    }),
                                },
                                {
                                    text: localize({
                                        key: 'TravelTypeField.driveTimeTraffic.update.refreshAll.txt',
                                    }),
                                },
                            ],
                        },
                    ], disabled: props.feature.traffic !== DriveTimeTrafficType.HISTORICAL_AVERAGE &&
                        props.feature.traffic !== DriveTimeTrafficType.LIVE, icon: { icon: Refresh }, onItemSelected: (location) => {
                        switch (location[0][1]) {
                            case 0: // update single feature
                                props.onTrafficRefresh(false);
                                break;
                            case 1: // update all features
                                props.onTrafficRefresh(true);
                                break;
                        }
                    }, tooltip: localize({ key: 'TravelTypeField.driveTimeTraffic.update.tooltip.txt' }), className: cn.formMenuButton, variant: ButtonVariant.Light })),
            props.feature.traffic === DriveTimeTrafficType.LIVE ? (React.createElement(SelectWithLabels, { label: localize({ key: 'TravelTypeField.traffic.offset.txt' }), selectedIndex: driveTimeTrafficOffsetIndex, onSelected: (selectedIndex) => {
                    if (typeof selectedIndex !== 'number') {
                        return;
                    }
                    props.onTrafficTimeChange({ hour: driveTimeTrafficOffsetTypes[selectedIndex].key });
                }, values: driveTimeTrafficOffsetTypes, fieldWidth: '100%', className: cn.formField })) : null,
            props.feature.traffic === DriveTimeTrafficType.HISTORICAL_AVERAGE ? (React.createElement(CompositeInputWithLabels, { label: localize({ key: 'TravelTypeField.traffic.daytime.txt' }), buttonDisplay: DateTime, fieldWidth: '100%', className: cn.formField, initialValue: props.feature.trafficTime, onChange: props.onTrafficTimeChange })) : null)) : null));
};
